// src/components/scenes/IntroPairingScene.js
/* eslint-disable no-console */
import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useScene } from "../../state/SceneContext";
import { setSceneHandlers } from "../../input/remote";
import logoUrl from "../../assets/logo-filled.png";
import QRCode from "qrcode";
import { createTvSession, watchApproval, exchangeForToken } from "../../lib/pairingClient";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../firebase";
import { handleThirdPartyAPICalls } from "../../services/authService";
import { getAuthToken } from "../../lib/storage/authStorage";

export default function IntroPairingScreen() {
  const { setScene } = useScene();

  // Timings
  const INTRO_MS = 3000;       // how long to keep logo intro on screen
  const FADE_IN_MS = 1200;     // logo fade/scale in
  const FADE_OUT_MS = 380;     // logo fade out when skipping to greeting

  // Layout
  const SCENE_W = 1920;
  const SCENE_H = 1080;
  const PAD_LEFT = 120;
  const LEFT_COL_W = 640;
  const COL_GAP = 24;
  const RIGHT_COL_W = 960;

  // Logo geometry
  const LOGO_W_INTRO = 460;
  const LOGO_W_TEACH = 420;
  const centerX = SCENE_W / 2;
  const targetX = PAD_LEFT + LEFT_COL_W / 2;
  const MOVE_X = targetX - centerX;

  const [imgLoaded, setImgLoaded] = useState(false);
  const [phase, setPhase] = useState("intro"); // 'intro' | 'teach'
  const [session, setSession] = useState(null); // { sessionId, code/pairingCode/pin/otp, secret }
  const [fadeOutLogo, setFadeOutLogo] = useState(false); // fade out only on skip
  const timersRef = useRef([]);
  const unsubApprovalRef = useRef(null);

  // Track auth state (hydrates after refresh). Also trust local bearer token.
  // Values: 'unknown' | 'signedIn' | 'signedOut'
  const [authStatus, setAuthStatus] = useState(() => {
    const hasLocalToken = Boolean(getAuthToken());
    return hasLocalToken ? "signedIn" : "unknown";
  });

  const prefersReduced =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)")?.matches;

  const goGreeting = React.useCallback(
    () => setScene({ name: "greeting", params: { resetRowMemory: true } }),
    [setScene]
  );

  // Preload logo
  useEffect(() => {
    const img = new Image();
    img.src = logoUrl;
    const onLoad = () => setImgLoaded(true);
    img.addEventListener("load", onLoad);
    return () => img.removeEventListener("load", onLoad);
  }, []);

  // Subscribe to Firebase auth hydration (handles page refresh correctly)
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setAuthStatus(u ? "signedIn" : "signedOut");
    });
    return () => unsub();
  }, []);

  // After intro logo:
  // - If signed in (Firebase) OR we have a local token AND Firebase hasn't said "signedOut", skip to greeting.
  // - Otherwise, enter pairing ("teach").
  useEffect(() => {
    if (phase !== "intro") return;

    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];

    const delay = prefersReduced ? 800 : INTRO_MS;

    const t = setTimeout(() => {
      const hasLocalToken = Boolean(getAuthToken());
      // Only trust a local token if Firebase hasn't explicitly said "signedOut".
      const tokenIsTrusted = authStatus !== "signedOut" && hasLocalToken;

      const shouldSkip = authStatus === "signedIn" || tokenIsTrusted;

      if (shouldSkip) {
        if (prefersReduced) {
          goGreeting();
        } else {
          setFadeOutLogo(true);
          const t2 = setTimeout(goGreeting, FADE_OUT_MS);
          timersRef.current.push(t2);
        }
      } else {
        setPhase("teach");
      }
    }, delay);

    timersRef.current.push(t);
    return () => timersRef.current.forEach(clearTimeout);
  }, [phase, authStatus, prefersReduced, goGreeting]);

  // Allow exiting the app from this screen via Back
  useEffect(() => {
    setSceneHandlers({
      onBack: () => {
        window.dispatchEvent(new CustomEvent("request-exit-app"));
      },
      onEnter: null,
      onLeft: null,
      onRight: null,
      onUp: null,
      onDown: null,
    });
    return () => setSceneHandlers({});
  }, []);

  // Wait for Firebase Auth user after token exchange (pairing)
  const waitForAuthUser = () =>
    new Promise((resolve) => {
      if (auth.currentUser) return resolve(auth.currentUser);
      const unsub = onAuthStateChanged(auth, (u) => {
        unsub();
        resolve(u || null);
      });
    });

  // Helper: start or restart a pairing session and begin watching for approval
  const startPairingSession = React.useCallback(async () => {
    unsubApprovalRef.current?.();
    setSession(null);
    try {
      const sess = await createTvSession({
        appVersion: "tv-1.0.0",
        model: navigator.userAgent,
      });
      setSession(sess);

      unsubApprovalRef.current = watchApproval(sess.sessionId, {
        onApproved: async () => {
          try {
            await exchangeForToken(sess.sessionId, sess.secret); // signs in TV
            const user = await waitForAuthUser();
            if (user) {
              try {
                await handleThirdPartyAPICalls(user); // sync before greeting/home
              } catch (e) {
                console.error("Third-party sync failed after pairing. Continuing.", e);
              }
            }
            goGreeting();
          } catch (e) {
            console.error("Exchange failed", e);
          }
        },
        onExpired: () => {
          console.warn("[pairing] session expired; restarting");
          // Auto-restart a new session (no regenerate button needed)
          startPairingSession();
        },
        onError: (e) => {
          console.error("[pairing] watchApproval error", e);
        },
      });
    } catch (e) {
      console.error("[pairing] createTvSession failed:", e);
    }
  }, [goGreeting]);

  // TEACH (pairing) phase: only when we did NOT skip
  useEffect(() => {
    if (phase !== "teach") return;
    startPairingSession();
    return () => unsubApprovalRef.current?.();
  }, [phase, startPairingSession]);

  const styles = `
    @keyframes kbeanFadeScaleIn {
      0%   { opacity: 0; transform: scale(0.92); }
      60%  { opacity: 1; transform: scale(1.012); }
      100% { opacity: 1; transform: scale(1); }
    }
    @media (prefers-reduced-motion: reduce) {
      .kbean-logo, .kbean-panel { animation: none !important; transition: none !important; }
    }
  `;

  const inTeach = phase === "teach";

  // Support multiple possible field names from backend
  const displayCode =
    session?.code ??
    session?.pairingCode ??
    session?.pin ??
    session?.otp ??
    null;

  // Use current domain for the QR target; omit scheme in the on-screen text
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "https://kbean.tv";
  const host =
    typeof window !== "undefined" && window.location?.host
      ? window.location.host
      : "kbean.tv";

  const qrTarget = displayCode
    ? `${origin}/pair?code=${encodeURIComponent(displayCode)}`
    : ""; // QR will render blank while loading

  const prettyDisplayUrl = `${host}/pair`; // shown on-screen without https://

  const isGenerating = inTeach && !displayCode;

  return (
    <Box
      className="scene"
      sx={{
        width: `${SCENE_W}px`,
        height: `${SCENE_H}px`,
        color: "common.white",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <style>{styles}</style>

      {/* LOGO (always shown; fades out only when skipping pairing) */}
      <Box
        sx={{
          position: "absolute",
          top: `${SCENE_H / 1.9}px`,
          left: `${SCENE_W / 2}px`,
          transform: `translate(-50%, -50%) translateX(${
            inTeach ? MOVE_X : 0
          }px) scale(${inTeach ? LOGO_W_TEACH / LOGO_W_INTRO : 1})`,
          transition: prefersReduced
            ? "none"
            : "transform 680ms cubic-bezier(.22,.84,.22,1), opacity 380ms ease",
          willChange: "transform, opacity",
          opacity: fadeOutLogo ? 0 : 1,
        }}
      >
        <Box
          className="kbean-logo"
          component="img"
          src={logoUrl}
          alt="KBEAN"
          onLoad={() => setImgLoaded(true)}
          sx={{
            width: inTeach ? `${LOGO_W_TEACH}px` : `${LOGO_W_INTRO}px`,
            height: "auto",
            opacity: imgLoaded ? 1 : 0,
            animation:
              !prefersReduced && imgLoaded && !inTeach
                ? `kbeanFadeScaleIn ${FADE_IN_MS}ms cubic-bezier(.2,.8,.2,1) 1 both`
                : "none",
          }}
        />
      </Box>

      {/* Pairing panel shows ONLY if signed out after hydration and after intro */}
      {inTeach && (
        <Box
          className="kbean-panel"
          sx={{
            position: "absolute",
            top: `${(SCENE_H - 320) / 2}px`,
            left: `${PAD_LEFT + LEFT_COL_W + COL_GAP}px`,
            width: `${RIGHT_COL_W}px`,
            height: "520px",
            transform: "translateX(24px)",
            opacity: 0,
            animation: prefersReduced ? "none" : "kbeanIn 360ms ease-out 1 both",
          }}
        >
          <style>{`@keyframes kbeanIn { from { opacity:0; transform:translateX(24px);} to {opacity:1; transform:translateX(0);} }`}</style>

          <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
            <QRCodeCanvas text={qrTarget} size={300} />

            <Box sx={{ width: "36px" }} />

            <Box sx={{ minWidth: "404px" }}>
              {isGenerating ? (
                <>
                  <Typography sx={{ fontSize: "44px", letterSpacing: "0.6px", lineHeight: 1.12, fontWeight: 600 }}>
                    Generating pairing code…
                  </Typography>
                  <Box sx={{ height: "14px" }} />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <CircularProgress size={30} thickness={4} />
                    <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: "22px" }}>
                      Please wait
                    </Typography>
                  </Box>
                </>
              ) : (
                <>
                  <Typography sx={{ fontSize: "44px", letterSpacing: "0.6px", lineHeight: 1.12, fontWeight: 600 }}>
                    Sign in with your phone
                  </Typography>
                  <Box sx={{ height: "10px" }} />
                  <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: "24px" }}>
                    Scan the QR or visit
                  </Typography>
                  <Box sx={{ height: "6px" }} />
                  <Typography sx={{ fontWeight: 800, lineHeight: 1.05, fontSize: "44px" }}>
                    {prettyDisplayUrl}
                  </Typography>
                  <Box sx={{ height: "18px" }} />
                  <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: "22px" }}>
                    and enter this code:
                  </Typography>
                  <Box sx={{ height: "6px" }} />
                  <Typography
                    sx={{
                      fontVariantNumeric: "tabular-nums",
                      letterSpacing: "12px",
                      lineHeight: 1.08,
                      fontWeight: 800,
                      fontSize: "88px",
                    }}
                  >
                    {displayCode ?? "••••••"}
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

function QRCodeCanvas({ text, size = 300 }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    (async () => {
      try {
        if (!canvasRef.current) return;
        await QRCode.toCanvas(canvasRef.current, text || "about:blank", {
          width: size,
          margin: 1,
          errorCorrectionLevel: "M",
        });
      } catch (e) {
        console.error("[pairing] QR render error", e);
      }
    })();
  }, [text, size]);
  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      aria-label="QR code"
      style={{
        width: size,
        height: size,
        background: "#fff",
        borderRadius: 8,
        display: "block",
      }}
    />
  );
}